import BrowserIcon from 'LuxKit/Icons/custom/BrowserIcon'
import type { IntlShape } from 'react-intl'
import { defineMessages } from 'react-intl'

interface AirlineLogo {
  id: string
  alt: string
  width: number
}

interface AirlineLogoConfig {
  airlineName: string
  id: string
  width: number
}

interface RegionStep {
  title: string
  text: string
  image: string
}

export interface RegionContent {
  text: Array<string>
  steps?: Array<RegionStep>
  airlineLogos?: Array<AirlineLogo>
  slug?: string
}

const messages = defineMessages({
  airlineLogoAlt: {
    id: 'aBiU32b1jTSLRj3apo7r',
    defaultMessage: '{airlineName} logo',
    description: 'Alternative text for airline logo images on flights landing pages',
  },
  commonIntroText: {
    id: 'Nmhp8YGa10juJ7G9jJML',
    defaultMessage: 'Luxury Escapes now provides your full holiday experience. From hotels, tours and flights to travel protection and experiences, the world’s best holidays have never been easier to book.',
    description: 'Introductory paragraph on flights landing pages explaining the bundled holiday offering',
  },
  singaporeIntroText: {
    id: 'YliYNUr85r3AB9YquX6w',
    defaultMessage: 'Luxury Escapes now provides your full holiday experience. From hotels to tours and flights to experiences, the world’s best holidays have never been easier to book.',
    description: 'Singapore-specific introductory paragraph on flights landing pages explaining the bundled holiday offering',
  },
  commonAirlineText: {
    id: 'RND73H2nL8LhbJZR5GCN',
    defaultMessage: 'Whether you are a loyal frequent flyer member, prefer a select airline or simply want the best value fare, we have you covered.',
    description: 'Paragraph on flights landing pages describing flight selection flexibility',
  },
  selectAvailabilityTitle: {
    id: 'xilCTCLOaA4aTLiSEu-P',
    defaultMessage: 'Select ‘Check Availability’ for Holiday + Flights',
    description: 'Step title explaining how to start a holiday and flights booking on flights landing pages',
  },
  selectAvailabilityText: {
    id: 'vWhC5l0r2FGgOpGvXsiQ',
    defaultMessage: 'Now when you browse Luxury Escapes packages you have the choice between a holiday only package and a holiday + flights option.',
    description: 'Step description explaining the holiday and flights package option on flights landing pages',
  },
  travellersAirportTitle: {
    id: 'iXHHtyfrhakoXHuZnqRm',
    defaultMessage: 'Add your number of travellers and select your departure airport',
    description: 'Step title explaining traveller and departure airport selection on flights landing pages',
  },
  travellersAirportText: {
    id: 'b1tUoAHbfT9WSSrEvjmc',
    defaultMessage: 'Select your number of travellers the same way you do when booking a holiday package and select your departure airport from the dropdown list.',
    description: 'Step description explaining traveller and departure airport selection on flights landing pages',
  },
  checkInDateTitle: {
    id: '6FchFoMMrdRnIlQWIlaV',
    defaultMessage: 'Select your check-in date',
    description: 'Step title explaining travel month and year selection on flights landing pages',
  },
  checkInDateText: {
    id: 'BCsv2Fb1EdkbpgSF-2cF',
    defaultMessage: 'Choose which month and year you want to travel in. You will be able to easily compare prices for the next 12-months to find the best time to travel.',
    description: 'Standard step description explaining travel month and year selection on flights landing pages',
  },
  singaporeCheckInDateText: {
    id: '3QeNcAwgt6BSPaESNPK-',
    defaultMessage: 'Choose which month and year you want to travel in. You will be able to easily compare prices for up to 12-months in advance to find the best time to travel.',
    description: 'Singapore-specific step description explaining travel month and year selection on flights landing pages',
  },
  chooseFlightsTitle: {
    id: 'V-BJbly2FFp8aB0A5dqj',
    defaultMessage: 'Choose flights from your favourite airline',
    description: 'Step title explaining airline selection on flights landing pages',
  },
  chooseFlightsText: {
    id: 'Y-akExveOM5XMLPb3KHJ',
    defaultMessage: 'Browse the available flight times and prices within your chosen travel dates. Select your exact departure and return date.',
    description: 'Step description explaining airline and flight selection on flights landing pages',
  },
  reviewBookingTitle: {
    id: 'dQudyMdcfGGRLv0L4mth',
    defaultMessage: 'Review your booking before checking out',
    description: 'Step title explaining final review before checkout on flights landing pages',
  },
  reviewBookingText: {
    id: '-mq_ZUG5jx1POFR_yXLY',
    defaultMessage: 'Take a moment to review your chosen holiday package and flight details before checking out securely through our website.',
    description: 'Step description explaining final review before checkout on flights landing pages',
  },
  indiaChoosePackageTitle: {
    id: 'C_zMCUcbS_5ZIoeP2nyM',
    defaultMessage: 'Choose your package options and select ‘Book now’',
    description: 'India-specific step title explaining how to start a holiday and flights booking on flights landing pages',
  },
  indiaChoosePackageText: {
    id: 'XCYE3E8OpE7kiI6pq5dF',
    defaultMessage: 'When you browse Luxury Escapes packages you have the choice between a holiday-only package and a holiday+flights package. Simply choose your number of travellers, travel dates and departure airport.',
    description: 'India-specific step description explaining the holiday and flights package option on flights landing pages',
  },
  indiaChooseFlightsText: {
    id: 'QvHqcAPDbRN9BWh2QYmX',
    defaultMessage: 'Browse the available flight times and prices within your chosen travel dates.',
    description: 'India-specific step description explaining flight selection on flights landing pages',
  },
  indiaReviewBookingTitle: {
    id: 'WrfhTmOxhAgLzAD74bCt',
    defaultMessage: 'Review your booking before finalising payment',
    description: 'India-specific step title explaining final review before payment on flights landing pages',
  },
  indiaReviewBookingText: {
    id: 'KDjkWuAk0FqyilD8yWof',
    defaultMessage: 'Take a moment to review your holiday+flights package details before checking out securely using our website.',
    description: 'India-specific step description explaining final review before payment on flights landing pages',
  },
  headerTitle: {
    id: 'JR-_m-NuyxyDR-QCd8e1',
    defaultMessage: 'Flights Have Landed',
    description: 'Hero title on the promotional flights landing page',
  },
  headerSubtitle: {
    id: 'HyKOmG9Sbati6R3YpKZm',
    defaultMessage: 'Book flights and escapes all in the one place',
    description: 'Hero subtitle on the promotional flights landing page',
  },
  mainContentTitle: {
    id: 'mBH7XJE_T3-BOFhwmUcK',
    defaultMessage: 'Fly With The World’s Best Airlines',
    description: 'Heading for the airline section on the promotional flights landing page',
  },
  howItWorksTitle: {
    id: '11kN164YLFhNl9lE3kHi',
    defaultMessage: 'How It Works',
    description: 'Heading for the how it works section on the promotional flights landing page',
  },
  howItWorksSubtitle: {
    id: 'qok6bh2yZBiQ464S3931',
    defaultMessage: 'When booking your next Luxury Escape, you will now have access to all major airlines. The best part? You can view flights up to one year in advance, making it easy to compare the best prices and dates before you book.',
    description: 'Subtitle for the how it works section on the promotional flights landing page',
  },
  feedbackTitle: {
    id: '0ae3QmJA-S4pn70jLMgM',
    defaultMessage: 'Give feedback online',
    description: 'Heading for the feedback section on the promotional flights landing page',
  },
  feedbackCtaText: {
    id: '6qRmML1LvgxV_tzeHKe9',
    defaultMessage: 'Start Survey Now',
    description: 'Call to action text for the feedback survey on the promotional flights landing page',
  },
})

const airlineLogoConfigs: Record<string, AirlineLogoConfig> = {
  qantas: {
    airlineName: 'Qantas',
    id: 'carrier-qf_brkc30',
    width: 100,
  },
  emirates: {
    airlineName: 'Emirates',
    id: 'carrier-ek_ig06eg',
    width: 100,
  },
  etihad: {
    airlineName: 'Etihad Airways',
    id: 'EYAIRWAYS_1019_CMYK-01_q0fjr0',
    width: 100,
  },
  thai: {
    airlineName: 'Thai',
    id: 'carrier-tg_r1bpaf',
    width: 100,
  },
  indonasia: {
    airlineName: 'Garuda Indonesia',
    id: 'carrier-ga_sxskqs',
    width: 100,
  },
  virgin: {
    airlineName: 'Virgin Australia',
    id: 'carrier-va_ax3sgz',
    width: 100,
  },
  singapore: {
    airlineName: 'Singapore Airlines',
    id: 'carrier-sq_bqotar',
    width: 100,
  },
  malaysia: {
    airlineName: 'Malaysia Airlines',
    id: 'carrier-mh_ammdag',
    width: 100,
  },
  airnz: {
    airlineName: 'Air New Zealand',
    id: '9ch84i3xz57lwu0z840u',
    width: 100,
  },
  japan: {
    airlineName: 'Japan Airlines',
    id: 'carrier-jal_gfcrd9',
    width: 100,
  },
  americanAirlines: {
    airlineName: 'American Airlines',
    id: 'y0j9vk7dxig1rrzjguuf',
    width: 100,
  },
  unitedAirlines: {
    airlineName: 'United Airlines',
    id: 'vykucynxbclgsjaa949',
    width: 100,
  },
  delta: {
    airlineName: 'Delta Airlines',
    id: 'zhufgg31ihna5l2z47l',
    width: 100,
  },
  alaskan: {
    airlineName: 'Alaskan Airlines',
    id: 'xmih9cs6e1wyy30azip',
    width: 100,
  },
  airCanada: {
    airlineName: 'Air Canada',
    id: '77c38x6brbnupv57jjct',
    width: 100,
  },
  westjet: {
    airlineName: 'Westjet Airlines',
    id: 'drht1poxvlg5in7r45ak',
    width: 100,
  },
  jetBlue: {
    airlineName: 'JetBlue Airlines',
    id: '2kv6a8ndyip7pqwuhqv',
    width: 100,
  },
  copa: {
    airlineName: 'Copa Airlines',
    id: 'kkaehz57qlnyijqbozn',
    width: 100,
  },
  britishAirways: {
    airlineName: 'British Airways',
    id: 'us3d83jbrp137rt77dl',
    width: 100,
  },
  qatar: {
    airlineName: 'Qatar Airways',
    id: 'vun4q04779g9juj24z4l',
    width: 100,
  },
  ryanair: {
    airlineName: 'Ryanair',
    id: 'rygurdhykjubb0axap63',
    width: 100,
  },
  virginAtlantic: {
    airlineName: 'Virgin Atlantic',
    id: 'rmue2bbnal5y314pg1k',
    width: 100,
  },
  easyJet: {
    airlineName: 'EasyJet',
    id: 'r1y2d49j6cmz3f2rubji',
    width: 100,
  },
  cathayPacific: {
    airlineName: 'Cathay Pacific',
    id: 'if0r6e2xrel6e0y9q7b',
    width: 100,
  },
  evaAir: {
    airlineName: 'EVA Air',
    id: 'bvq181twm7xzdwlublrs',
    width: 100,
  },
  hongKongExpress: {
    airlineName: 'Hong Kong Express Airways',
    id: 'jq3yuptp9s05iwywk4x7',
    width: 100,
  },
  indigo: {
    airlineName: 'IndiGo',
    id: 's8rjj8hq2xbz107fz',
    width: 100,
  },
  spiceJet: {
    airlineName: 'Spice Jet',
    id: 'alpm0r9x6w7zqgs5gb',
    width: 100,
  },
  airAsia: {
    airlineName: 'Air Asia',
    id: '1aljbjddceizx9b4ceso',
    width: 100,
  },
  scoot: {
    airlineName: 'Scoot',
    id: 'pgsrdvxjgeqg8un578ar',
    width: 100,
  },
  airIndiaExpress: {
    airlineName: 'Air India Express',
    id: 'd8604lkqinwyff60ip4v',
    width: 100,
  },
  goFirst: {
    airlineName: 'Go First',
    id: '12y5v6go3b7r1gwx2h9',
    width: 100,
  },
  vietJet: {
    airlineName: 'VietJet',
    id: 's6x92ypwqat2h5pqwkp',
    width: 100,
  },
  airArabia: {
    airlineName: 'Air Arabia',
    id: 'r1oj3kg3v6f3ltm6j6t',
    width: 100,
  },
}

const getAirlineLogo = (intl: IntlShape, key: keyof typeof airlineLogoConfigs): AirlineLogo => {
  const logo = airlineLogoConfigs[key]

  return {
    id: logo.id,
    alt: intl.formatMessage(messages.airlineLogoAlt, { airlineName: logo.airlineName }),
    width: logo.width,
  }
}

const getStandardSteps = (
  intl: IntlShape,
  images: [string, string, string, string, string],
): Array<RegionStep> => [
  {
    title: intl.formatMessage(messages.selectAvailabilityTitle),
    text: intl.formatMessage(messages.selectAvailabilityText),
    image: images[0],
  },
  {
    title: intl.formatMessage(messages.travellersAirportTitle),
    text: intl.formatMessage(messages.travellersAirportText),
    image: images[1],
  },
  {
    title: intl.formatMessage(messages.checkInDateTitle),
    text: intl.formatMessage(messages.checkInDateText),
    image: images[2],
  },
  {
    title: intl.formatMessage(messages.chooseFlightsTitle),
    text: intl.formatMessage(messages.chooseFlightsText),
    image: images[3],
  },
  {
    title: intl.formatMessage(messages.reviewBookingTitle),
    text: intl.formatMessage(messages.reviewBookingText),
    image: images[4],
  },
]

const getSingaporeSteps = (intl: IntlShape): Array<RegionStep> => [
  {
    title: intl.formatMessage(messages.selectAvailabilityTitle),
    text: intl.formatMessage(messages.selectAvailabilityText),
    image: 'Image_1_SG_nxhgmn',
  },
  {
    title: intl.formatMessage(messages.travellersAirportTitle),
    text: intl.formatMessage(messages.travellersAirportText),
    image: 'Image_2_SG_zej3yr',
  },
  {
    title: intl.formatMessage(messages.checkInDateTitle),
    text: intl.formatMessage(messages.singaporeCheckInDateText),
    image: 'Image_3_SG_qoyryp',
  },
  {
    title: intl.formatMessage(messages.chooseFlightsTitle),
    text: intl.formatMessage(messages.chooseFlightsText),
    image: 'Image_4_SG_rxi8sl',
  },
  {
    title: intl.formatMessage(messages.reviewBookingTitle),
    text: intl.formatMessage(messages.reviewBookingText),
    image: 'Image_5_SG_nqet62',
  },
]

const getIndiaSteps = (intl: IntlShape): Array<RegionStep> => [
  {
    title: intl.formatMessage(messages.indiaChoosePackageTitle),
    text: intl.formatMessage(messages.indiaChoosePackageText),
    image: 'aqyg3i3q7zk1pjotfds',
  },
  {
    title: intl.formatMessage(messages.chooseFlightsTitle),
    text: intl.formatMessage(messages.indiaChooseFlightsText),
    image: 'ikpsmasr0mfy8vyx0v6',
  },
  {
    title: intl.formatMessage(messages.indiaReviewBookingTitle),
    text: intl.formatMessage(messages.indiaReviewBookingText),
    image: '6y18jh9gbbliz1g88tsm',
  },
]

export const getFlightLandingPageRegionContent = (intl: IntlShape): Record<string, RegionContent> => {
  const airlineLogos = {
    qantas: getAirlineLogo(intl, 'qantas'),
    emirates: getAirlineLogo(intl, 'emirates'),
    etihad: getAirlineLogo(intl, 'etihad'),
    thai: getAirlineLogo(intl, 'thai'),
    indonasia: getAirlineLogo(intl, 'indonasia'),
    virgin: getAirlineLogo(intl, 'virgin'),
    singapore: getAirlineLogo(intl, 'singapore'),
    malaysia: getAirlineLogo(intl, 'malaysia'),
    airnz: getAirlineLogo(intl, 'airnz'),
    japan: getAirlineLogo(intl, 'japan'),
    americanAirlines: getAirlineLogo(intl, 'americanAirlines'),
    unitedAirlines: getAirlineLogo(intl, 'unitedAirlines'),
    delta: getAirlineLogo(intl, 'delta'),
    alaskan: getAirlineLogo(intl, 'alaskan'),
    airCanada: getAirlineLogo(intl, 'airCanada'),
    westjet: getAirlineLogo(intl, 'westjet'),
    jetBlue: getAirlineLogo(intl, 'jetBlue'),
    copa: getAirlineLogo(intl, 'copa'),
    britishAirways: getAirlineLogo(intl, 'britishAirways'),
    qatar: getAirlineLogo(intl, 'qatar'),
    cathayPacific: getAirlineLogo(intl, 'cathayPacific'),
    easyJet: getAirlineLogo(intl, 'easyJet'),
    evaAir: getAirlineLogo(intl, 'evaAir'),
    hongKongExpress: getAirlineLogo(intl, 'hongKongExpress'),
    indigo: getAirlineLogo(intl, 'indigo'),
    spiceJet: getAirlineLogo(intl, 'spiceJet'),
    airAsia: getAirlineLogo(intl, 'airAsia'),
    scoot: getAirlineLogo(intl, 'scoot'),
    airIndiaExpress: getAirlineLogo(intl, 'airIndiaExpress'),
    goFirst: getAirlineLogo(intl, 'goFirst'),
    vietJet: getAirlineLogo(intl, 'vietJet'),
    airArabia: getAirlineLogo(intl, 'airArabia'),
  }

  const commonText = [
    intl.formatMessage(messages.commonIntroText),
    intl.formatMessage(messages.commonAirlineText),
  ]

  return {
    AU: {
      text: commonText,
      steps: getStandardSteps(intl, [
        'check-availability-v2_sqaead',
        'capacity-v2_zvfoho',
        'check-in-v2_gkcvi7',
        'available-flight-gds-v2_hjleqy',
        'review-order-gds-v2_rkq1tg',
      ]),
      airlineLogos: [airlineLogos.qantas, airlineLogos.emirates, airlineLogos.etihad, airlineLogos.thai, airlineLogos.indonasia, airlineLogos.virgin, airlineLogos.singapore, airlineLogos.malaysia],
      slug: 'flights-faq',
    },
    GB: {
      text: commonText,
      airlineLogos: [airlineLogos.emirates, airlineLogos.qatar, airlineLogos.etihad, airlineLogos.singapore, airlineLogos.malaysia, airlineLogos.britishAirways, airlineLogos.cathayPacific, airlineLogos.easyJet],
    },
    NZ: {
      text: commonText,
      steps: getStandardSteps(intl, [
        'Image_1_NZ_phga5s',
        'Image_2_NZ_zicb3i',
        'Image_3_NZ_vv0wft',
        'Image_4_NZ_kf7dfc',
        'Image_5_NZ_kb2e99',
      ]),
      airlineLogos: [airlineLogos.airnz, airlineLogos.emirates, airlineLogos.etihad, airlineLogos.thai, airlineLogos.indonasia, airlineLogos.virgin, airlineLogos.singapore, airlineLogos.malaysia],
    },
    SG: {
      text: [
        intl.formatMessage(messages.singaporeIntroText),
        intl.formatMessage(messages.commonAirlineText),
      ],
      steps: getSingaporeSteps(intl),
      airlineLogos: [airlineLogos.singapore, airlineLogos.indonasia, airlineLogos.malaysia, airlineLogos.thai, airlineLogos.japan, airlineLogos.emirates, airlineLogos.etihad, airlineLogos.qantas],
    },
    HK: {
      text: commonText,
      steps: getStandardSteps(intl, [
        'hk-flights-1_bbgorh',
        'hk-flights-2_quooft',
        'hk-flights-3_tfhy7m',
        'hk-flights-4_q9e3ti',
        'hk-flights-5_ti61lo',
      ]),
      airlineLogos: [airlineLogos.cathayPacific, airlineLogos.singapore, airlineLogos.emirates, airlineLogos.thai, airlineLogos.evaAir, airlineLogos.indonasia, airlineLogos.hongKongExpress, airlineLogos.qatar],
    },
    US: {
      text: commonText,
      steps: getStandardSteps(intl, [
        'check-availability-v2_sqaead',
        'capacity-v2_zvfoho',
        'check-in-v2_gkcvi7',
        'available-flight-gds-v2_hjleqy',
        'review-order-gds-v2_rkq1tg',
      ]),
      airlineLogos: [airlineLogos.americanAirlines, airlineLogos.unitedAirlines, airlineLogos.delta, airlineLogos.alaskan, airlineLogos.airCanada, airlineLogos.westjet, airlineLogos.jetBlue, airlineLogos.copa],
      slug: 'us-flights-faq',
    },
    IN: {
      text: commonText,
      steps: getIndiaSteps(intl),
      airlineLogos: [airlineLogos.indigo, airlineLogos.spiceJet, airlineLogos.airAsia, airlineLogos.scoot, airlineLogos.airIndiaExpress, airlineLogos.goFirst, airlineLogos.vietJet, airlineLogos.airArabia],
      slug: 'in-flights-faq',
    },
  }
}

export const getFlightLandingPageContent = (intl: IntlShape) => ({
  header: {
    title: intl.formatMessage(messages.headerTitle),
    subTitle: intl.formatMessage(messages.headerSubtitle),
  },
  mainContent: {
    title: intl.formatMessage(messages.mainContentTitle),
  },
  howItWorks: {
    title: intl.formatMessage(messages.howItWorksTitle),
    subTitle: intl.formatMessage(messages.howItWorksSubtitle),
  },
  feedback: [
    {
      title: intl.formatMessage(messages.feedbackTitle),
      cta: [{
        text: intl.formatMessage(messages.feedbackCtaText),
        link: 'https://www.research.net/r/BZFNT6M',
      }],
      icon: <BrowserIcon width={55} height={46} colour="primary" />,
    },
  ],
})

export default getFlightLandingPageContent
